import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'NDS DataVault',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Dental data warehouse platform for U.S. DSOs. Aggregates Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS data for enterprise analytics, Power BI reporting, and data governance.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'National Data Solutions',
        url: 'https://nationaldatasolutions.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a dental data warehouse?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A dental data warehouse is a centralized repository that aggregates clinical, financial, and operational data from one or more dental practice management systems (PMS). It normalizes that data into a consistent schema, maintains historical records, and makes the data available for analytics tools like Power BI or Tableau. For a DSO, this enables enterprise reporting across all locations from a single source of truth — without relying on PMS-native reports that vary by system or location.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which PMS systems does NDS DataVault support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'NDS DataVault provides pre-built connectors for Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, and Xentrios. For DSOs using multiple PMS systems across their portfolio — common after acquisitions — DataVault normalizes data from all sources into a single warehouse schema so your analytics layer sees one consistent dataset regardless of which PMS each location uses.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does NDS DataVault work with Power BI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. NDS DataVault is designed with Power BI as the primary reporting layer. NDS provides pre-built Power BI report packages covering production, collections, AR aging, clinical metrics, provider performance, and scheduling efficiency. Custom KPI models and report development are available through the NDS advisory team for groups with specific reporting requirements.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does it take to implement NDS DataVault?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Implementation timeline depends on the number of locations, PMS types, and data history requirements. For a DSO using a single PMS across 10 to 30 locations, initial DataVault deployment with pre-built Power BI dashboards typically takes 4 to 8 weeks. Enterprise groups with multiple PMS types and complex data governance requirements typically plan for a 10 to 16 week phased rollout.',
          },
        },
      ],
    },
  ],
};

const features = [
  {
    icon: '🔌',
    name: 'Multi-PMS Extraction',
    desc: 'Pre-built connectors for Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, and Xentrios. Scheduled extraction pipelines pull data automatically — no manual exports from practice staff.',
  },
  {
    icon: '🔄',
    name: 'Data Normalization',
    desc: 'Dental data from different PMS systems uses different schemas, code formats, and patient identifiers. DataVault resolves those differences into a single consistent data model across your entire portfolio.',
  },
  {
    icon: '📊',
    name: 'Power BI Integration',
    desc: 'Pre-built Power BI report packages covering production, collections, AR aging, scheduling efficiency, provider performance, and clinical metrics. Deployed to your tenant and customizable by your team.',
  },
  {
    icon: '📅',
    name: 'Historical Trend Analysis',
    desc: 'DataVault maintains full historical data going back to your earliest PMS records. Track multi-year production trends, year-over-year payer performance, and long-term AR recovery rates.',
  },
  {
    icon: '🎯',
    name: 'Custom KPI Models',
    desc: 'Not every DSO measures success the same way. NDS works with your leadership team to define and build custom KPI models that reflect how your organization actually manages performance.',
  },
  {
    icon: '🛡️',
    name: 'Enterprise Governance',
    desc: 'Role-based data access, audit logging, HIPAA-compliant data handling, documented data lineage, and change management controls — built for PE-backed groups and enterprise DSOs that require governance rigor.',
  },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'Xentrios'];

export default function DentalDataWarehousePage() {
  const cmsPage = useCmsPage('xentrios', 'dental-data-warehouse');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Dental Data Warehouse for DSOs');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const featureItems = c(cmsPage, 'features', 'items', features);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>Dental Data Warehouse for DSOs</title>
        <meta
          name="description"
          content="National Data Solutions (NDS) provides a dental data warehouse for U.S. DSOs: DataVault aggregates Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS data for enterprise analytics and Power BI reporting."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental Data Warehouse</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* What is a dental data warehouse? */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Category</p>
          <h2>What is a dental data warehouse?</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            A dental data warehouse is the foundational analytics infrastructure that enterprise dental
            groups are missing from their technology stack.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div>
              <p>
                Every PMS stores data — but it stores it in a format optimized for that system's own
                workflows, not for cross-location analytics. Dentrix stores data differently than Eaglesoft.
                Eaglesoft stores data differently than Open Dental. And when you've grown through acquisitions
                and now operate three or four different PMS systems across your portfolio, you don't have a
                data strategy — you have a data problem.
              </p>
              <p style={{ marginTop: '1rem' }}>
                A dental data warehouse solves this by creating a centralized, normalized repository for all
                your clinical and financial data. ETL (extract, transform, load) pipelines pull data from
                each PMS on a scheduled basis, apply consistent transformations, and load it into a warehouse
                schema that your analytics tools can query directly.
              </p>
            </div>
            <div>
              <p>
                For reporting, this means one Power BI dashboard that shows production, collections, AR
                aging, and scheduling efficiency for all locations — regardless of which PMS each location
                uses. For financial planning, it means reliable historical data going back years. For
                compliance and audit purposes, it means documented data lineage and access controls.
              </p>
              <p style={{ marginTop: '1rem' }}>
                For a DSO that has been running reports out of each PMS individually and pasting them
                together in Excel, the shift to a unified data warehouse is a step-change in what your
                operations and finance teams can actually see and act on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NDS DataVault feature grid */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">NDS DataVault</p>
          <h2>The dental data warehouse built for enterprise groups.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            DataVault combines pre-built PMS connectors, a governed data model, and Power BI report
            packages into a single platform designed specifically for U.S. dental groups.
          </p>
          <div className="xen-features">
            {featureItems.map((f) => (
              <div key={f.name} className="xen-feature" style={{ textAlign: 'left' }}>
                <div className="xen-feature-icon" style={{ fontSize: '1.75rem' }}>{f.icon}</div>
                <h3 style={{ color: 'var(--xen-navy)' }}>{f.name}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PMS connector list */}
      <section className="xen-section xen-section-center xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Integrations</p>
          <h2>Connects to every major dental PMS.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            DataVault pre-built connectors eliminate custom integration work. Your PMS data starts flowing
            into the warehouse from day one of your implementation.
          </p>
          <div className="xen-integrations">
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NDS DataVault */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Why NDS DataVault</p>
          <h2>Why NDS DataVault for U.S. dental groups</h2>
          <div className="xen-two-col" style={{ marginTop: '2rem', gap: '3rem' }}>
            <div>
              <h3 style={{ color: '#c62828', marginBottom: '1rem' }}>What dental groups typically experience</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.9, color: '#444' }}>
                <li>Executive dashboards built in Excel, updated manually by someone on the finance team</li>
                <li>Different PMS systems across acquired practices with incompatible reporting formats</li>
                <li>PMS-native reports that can't aggregate across locations without export and re-import</li>
                <li>No historical data beyond what the current PMS can surface in its built-in reports</li>
                <li>IT projects to "build a data warehouse" that drag on for 12–18 months with no delivery</li>
                <li>Analytics consultants who build one-off reports but can't maintain a governed data model</li>
                <li>Power BI reports that break every time the PMS does a schema update</li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: '#388e3c', marginBottom: '1rem' }}>What NDS DataVault delivers</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 1.9, color: '#444' }}>
                <li>
                  <strong>Pre-built, maintained connectors</strong> — NDS owns the PMS connector layer,
                  so when Dentrix or Eaglesoft updates their schema, the connector gets updated — not your team
                </li>
                <li>
                  <strong>Dental-specific data model</strong> — built around how dental groups actually
                  measure production, collections, AR, and clinical performance
                </li>
                <li>
                  <strong>Power BI report packages</strong> — delivered and deployed to your Microsoft 365
                  tenant, customizable by your team
                </li>
                <li>
                  <strong>RevTech integration</strong> — InsightFlow, DenialIQ, and ReconIQ pull directly
                  from the DataVault layer, eliminating a separate reporting pipeline
                </li>
                <li>
                  <strong>Xentrios native connection</strong> — if you're running Xentrios as your PMS,
                  DataVault receives real-time data rather than scheduled batch extracts
                </li>
                <li>
                  <strong>Governance ready</strong> — audit logging, access controls, and data lineage
                  documentation suitable for PE reporting and board-level financial review
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See what DataVault can do for your enterprise reporting.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Tell us which PMS systems you're running and how many locations you operate. We'll show you
            exactly what a DataVault deployment looks like for your group.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
